import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { useTableBookingTicket } from '@/features/booking/hooks/useTableBookingTicket';
import { useLoadingContext, useAuth } from '@/hooks';
import { TableBookingSkeleton } from '@/components/skeleton';

import { MobileHeroSection } from './MobileHeroSection';
import { MobileTierRibbon } from './MobileTierRibbon';
import { MobileBookingCategoryHero } from './MobileBookingCategoryHero';
import { MobileAllowedAreaSection } from './MobileAllowedAreaSection';
import MobileTableFloatingSummary from './MobileTableFloatingSummary';
import MobileTableListView from './MobileTableListView';
import MobileTableSelectionPopup from './MobileTableSelectionPopup';
import { PerkCarousel } from '@/features/booking/components/PerkCarousel';
import { TimeSlotTabs, FloorPlanCarousel } from '@/features/booking/components';
import { useFloorPlans } from '@/features/booking/hooks/useFloorPlans';
import type { FloorPlanCarouselRef } from '@/features/booking/components/FloorPlanCarousel';
import type { FloorPlanResponse } from '@/features/booking/types/floorPlanTypes';

const TableBookingMobile: React.FC = () => {
  const { eventBookingCategoryId } = useParams<{
    eventBookingCategoryId: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoadingContext();
  const { isGuest, isAuthenticated } = useAuth();
  const scrollRef = useRef<HTMLDivElement>(null);

  const bookingSectionRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToIntro, setHasScrolledToIntro] = useState(false);

  // Extract cartType and cartItemId from query parameters
  const cartTypeParam = searchParams.get('cartType');
  const initialCartTab: 'main' | 'guest' | null = 
    cartTypeParam === 'guest' || cartTypeParam === 'main' ? cartTypeParam : null;
  const cartItemIdParam = searchParams.get('cartItemId');

  const {
    eventSchedule,
    eventBookingCategory,
    selections,
    selectedTable,
    selectedTimeSlot,
    loading,
    error,
    syncingCart,
    priceBreakdown,
    availableTables,
    currentSelection,
    adjustedAddOnSchedules,
    selectTable,
    selectTimeSlot,
    setGuestCountForSlot,
    handleAddOnToggleForSlot,
    setAddOnQuantityForSlot,
    syncCartItems,
    removeAllItems,
    incompleteCarts,
    selectedCartTab,
    currentCart,
    isLoadingCarts,
    hasCartItemForEBC,
    hasCartChanges,
    hasSelections,
    switchCartTab,
    tableSize,
    timeSlots,
    addOnErrorMessage,
    soldOutCartTables,
    getItemCountForTab,
  } = useTableBookingTicket(eventBookingCategoryId || '', initialCartTab, cartItemIdParam);

  const pageCurrency = eventBookingCategory?.currency;
  const timezone = eventSchedule?.timezone_id;

  const rawItemType = eventBookingCategory?.booking_category?.item_type || 'TABLE';
  const displayItemType = rawItemType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase());

  // Floor plan
  const bookingCategoryId = eventBookingCategory?.booking_category?.id;
  const { floorPlans, loading: floorPlansLoading } = useFloorPlans(bookingCategoryId);
  const floorPlanCarouselRef = useRef<FloorPlanCarouselRef>(null);
  const [currentFloorPlan, setCurrentFloorPlan] = useState<FloorPlanResponse | null>(null);

  const handleFloorPlanChange = useCallback((fp: FloorPlanResponse) => {
    setCurrentFloorPlan(fp);
  }, []);

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const handleTableClick = useCallback((tableNumber: string) => {
    selectTable(tableNumber);
    setIsPopupOpen(true);
  }, [selectTable]);


  // Tabs state for map vs list
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');

  // Compute timeslots that have user selections
  const slotsWithCounts = useMemo(() => {
    const counts = new Map<string, number>();
    selections.forEach(tableMap => {
      tableMap.forEach((sel, slotKey) => {
        if (sel.guestCount > 0) {
          counts.set(slotKey, (counts.get(slotKey) || 0) + 1);
        }
      });
    });
    return counts;
  }, [selections]);


  // Compute local vs in-cart tables for the currently selected timeslot
  const { inCartTables, localTables } = useMemo(() => {
    const inCart: string[] = [];
    const local: string[] = [];
    if (!selectedTimeSlot) return { inCartTables: inCart, localTables: local };

    selections.forEach((tableMap, tableNumber) => {
      const selection = tableMap.get(selectedTimeSlot);
      if (selection && selection.guestCount > 0) {
        if (selection.cartItemId) {
          inCart.push(tableNumber);
        } else {
          local.push(tableNumber);
        }
      }
    });

    return { inCartTables: inCart, localTables: local };
  }, [selections, selectedTimeSlot]);

  // Trigger loading state
  useEffect(() => {
    if (loading) {
      startLoading('table-booking-ticket-data');
    } else {
      stopLoading('table-booking-ticket-data');
    }
    
    return () => {
      stopLoading('table-booking-ticket-data');
    };
  }, [loading, startLoading, stopLoading]);

  // Detect when the booking section scrolls into view (both manual and programmatic)
  useEffect(() => {
    const container = scrollRef.current;
    const bookingEl = bookingSectionRef.current;
    if (!container || !bookingEl || hasScrolledToIntro) return;

    const checkVisibility = () => {
      const containerRect = container.getBoundingClientRect();
      const bookingRect = bookingEl.getBoundingClientRect();
      if (bookingRect.top < containerRect.bottom - 100) {
        setHasScrolledToIntro(true);
      }
    };

    // Check immediately in case the section is already visible
    checkVisibility();

    container.addEventListener('scroll', checkVisibility, { passive: true });
    return () => container.removeEventListener('scroll', checkVisibility);
  }, [hasScrolledToIntro, loading]);

  // Button Action handlers
  const getButtonText = (): string => {
    if (!hasSelections && !hasCartItemForEBC) {
      return 'Add to Cart';
    }
    if (hasSelections && hasCartItemForEBC && hasCartChanges) {
      return 'Update Cart';
    }
    if (!hasSelections && hasCartItemForEBC) {
      return 'Remove Items';
    }
    return 'Add to Cart';
  };

  const handleCartAction = async () => {
    if (!hasSelections && hasCartItemForEBC) {
      await removeAllItems();
      setIsPopupOpen(false);
      return;
    }

    if (!hasSelections && !hasCartItemForEBC) {
      return;
    }

    try {
      await syncCartItems();
      setIsPopupOpen(false);
    } catch (error) {
      console.error('Error syncing cart:', error);
    }
  };

  const handleBack = () => {
    if (eventSchedule?.event?.business?.handle && eventSchedule?.event?.id) {
      navigate(`/venues/${eventSchedule.event.business.handle}/events/${eventSchedule.event.id}`);
    } else {
      navigate(-1);
    }
  };

  const getSelectedAddOns = (): Record<string, number> => {
    if (!currentSelection) return {};
    
    const selected: Record<string, number> = {};
    currentSelection.addOnIdsPerGuest.forEach(guestAddOns => {
      guestAddOns.forEach(addOnId => {
        selected[addOnId] = (selected[addOnId] || 0) + 1;
      });
    });
    
    return selected;
  };

  const allFloorPlanItems = useMemo(() => {
    return floorPlans.flatMap(fp => fp.items.map(item => ({ ...item, floorPlanName: fp.name })));
  }, [floorPlans]);

  // Sort floor plans by total available seats for the selected slot (only when no cart items)
  const sortedFloorPlans = useMemo(() => {
    if (hasCartItemForEBC || floorPlans.length <= 1) return floorPlans;

    const availableSet = new Set(availableTables);

    const scored = floorPlans.map(fp => {
      const totalSeats = fp.items.reduce((sum, item) => {
        if (availableSet.has(item.table_number)) {
          return sum + (item.table_size || 0);
        }
        return sum;
      }, 0);
      return { fp, totalSeats };
    });

    scored.sort((a, b) => b.totalSeats - a.totalSeats);
    return scored.map(s => s.fp);
  }, [floorPlans, availableTables, hasCartItemForEBC]);

  // When a table is selected, navigate the carousel to the floor plan containing that table
  useEffect(() => {
    if (!selectedTable || sortedFloorPlans.length === 0) return;

    const targetIndex = sortedFloorPlans.findIndex((fp) =>
      fp.items.some((item) => item.table_number === selectedTable)
    );
    if (targetIndex >= 0) {
      floorPlanCarouselRef.current?.goToIndex(targetIndex);
    }
  }, [selectedTable, sortedFloorPlans]);

  // Mobile specific: when add is clicked, automatically add the tableSize as guestCount, or remove if already added
  const handleAddTableClick = () => {
    if (selectedTable && selectedTimeSlot && tableSize) {
      if (currentSelection && currentSelection.guestCount > 0) {
        setGuestCountForSlot(selectedTable, selectedTimeSlot, 0);
      } else {
        setGuestCountForSlot(selectedTable, selectedTimeSlot, tableSize);
      }
    }
  };

  // Mobile specific: Deselect table
  const handleBackFromPreview = () => {
    setIsPopupOpen(false);
  };

  if (loading) {
    return <TableBookingSkeleton />;
  }

  if (error || !eventSchedule || !eventBookingCategory) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Booking Not Found</h1>
          <p className="text-gray-400 mb-6">
            {error || 'The booking option you are looking for does not exist.'}
          </p>
          <button 
            onClick={handleBack}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const selectedAddOns = getSelectedAddOns();

  return (
    <div ref={scrollRef} className="bg-[#050505] fixed inset-0 text-white font-sans w-full max-w-[100vw] z-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-none">
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: { 
            background: '#1f2937', 
            color: '#fff',
            border: '1px solid #374151'
          },
          duration: 3000,
        }} 
      />
      
      {/* 1. Mobile Hero Section */}
      <MobileHeroSection 
        event={eventSchedule?.event} 
        business={eventBookingCategory?.booking_category?.business || eventSchedule?.event?.business} 
        isAuthenticated={isAuthenticated}
        scrollRef={scrollRef}
        onBack={() => {
          if (eventSchedule?.event?.business?.handle && eventSchedule?.event?.id && eventSchedule?.id) {
            navigate(`/venues/${eventSchedule.event.business.handle}/events/${eventSchedule.event.id}/booking/${eventSchedule.id}`);
          } else {
            navigate(-1);
          }
        }}
      />
      
      {/* Scrollable Main Content Below */}
      <main className="flex-1 pb-32 relative z-10 w-full">
        {/* Tier Ribbon - Sticky */}
        <div className="sticky top-16 z-40 bg-[#050505]">
          <MobileTierRibbon 
            tierClass={eventBookingCategory?.booking_category?.class}
            startTime={eventSchedule?.start_time}
            timezoneId={eventSchedule?.timezone_id}
          />
        </div>

        {/* 3. Booking Category Hero (Name, Desc, Price, Perks) */}
        {eventBookingCategory && (
          <MobileBookingCategoryHero 
            eventBookingCategory={eventBookingCategory as any} 
            isTableBooking={true}
          />
        )}

        {/* 4. Allowed Areas Carousel */}
        {eventBookingCategory?.booking_category?.allowed_areas && (
          <MobileAllowedAreaSection allowedAreas={eventBookingCategory.booking_category.allowed_areas as any} />
        )}

        {/* 5. Perks Carousel */}
        {eventBookingCategory?.booking_category?.perks && (
          <PerkCarousel perks={eventBookingCategory.booking_category.perks} />
        )}

        {/* Cart Tabs */}
        {!isLoadingCarts && incompleteCarts && incompleteCarts.length > 0 && (() => {
          const mainCart = incompleteCarts.find(c => !c.is_guest_cart);
          const guestCart = incompleteCarts.find(c => c.is_guest_cart);
          const mainCartHasItems = mainCart?.items && mainCart.items.length > 0;
          const guestCartHasItems = guestCart?.items && guestCart.items.length > 0;
          
          if (!mainCartHasItems && !guestCartHasItems) {
            return null;
          }
          
          const showTabs = !!guestCart && guestCartHasItems && (mainCartHasItems || !isGuest);
          
          if (!showTabs) {
            return null;
          }
          
          const showMainCartTab = mainCartHasItems || (guestCart && !isGuest);
          
          return (
            <div className="w-full px-4 pt-4 pb-2 border-b border-gray-800">
              <div className="flex gap-4">
                {showMainCartTab && (
                  <button
                    onClick={() => switchCartTab('main')}
                    className={`pb-2 font-medium transition-colors relative ${
                      selectedCartTab === 'main'
                        ? 'text-white border-b-2 border-blue-500'
                        : 'text-gray-400 hover:text-gray-300 border-transparent'
                    }`}
                  >
                    Main Cart{getItemCountForTab('main') > 0 ? ` (${getItemCountForTab('main')})` : ''}
                  </button>
                )}
                
                <button
                  onClick={() => switchCartTab('guest')}
                  className={`pb-2 font-medium transition-colors relative ${
                    selectedCartTab === 'guest'
                      ? 'text-white border-b-2 border-blue-500'
                      : 'text-gray-400 hover:text-gray-300 border-transparent'
                  }`}
                >
                  Guest Cart{getItemCountForTab('guest') > 0 ? ` (${getItemCountForTab('guest')})` : ''}
                </button>
              </div>
            </div>
          );
        })()}

        <div ref={bookingSectionRef} className="w-full pt-1 pb-1 scroll-mt-32">
          {/* TimeSlot Selector */}
          {timeSlots.length > 0 && eventBookingCategory?.custom_slot_duration && (
          <div className="mt-6 px-2">
            <TimeSlotTabs
              timeSlots={timeSlots}
              selectedTimeSlot={selectedTimeSlot}
              onTimeSlotSelect={selectTimeSlot}
              businessTimeZone={timezone}
              slotsWithCounts={slotsWithCounts}
              eventStartTime={eventSchedule?.start_time}
            />
          </div>
        )}

        {/* Map / List View Toggle */}
        {selectedTimeSlot && (
          <div className="w-full px-4 mt-6 flex justify-center z-10 relative">
            <div className="bg-[#111] rounded-full p-1 flex w-[80%] max-w-xs border border-white/10 shadow-lg relative overflow-hidden">
              {/* Highlight Pill */}
              <motion.div
                className="absolute top-1 bottom-1 w-[calc(50%-4px)] bg-blue-600 rounded-full shadow-md z-0 pointer-events-none"
                initial={false}
                animate={{
                  x: viewMode === 'list' ? '100%' : 0,
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
              <button
                onClick={() => setViewMode('map')}
                className={`relative z-10 flex-1 py-2 text-sm font-semibold rounded-full transition-colors duration-200 ${
                  viewMode === 'map' 
                    ? 'text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Map View
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`relative z-10 flex-1 py-2 text-sm font-semibold rounded-full transition-colors duration-200 ${
                  viewMode === 'list' 
                    ? 'text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                List View
              </button>
            </div>
          </div>
        )}

        {/* Map / List Content */}
        {selectedTimeSlot && viewMode === 'map' && (
          <div className="mt-4">
            <div className="px-4">
              {floorPlansLoading ? (
                <div className="w-full rounded-2xl bg-gray-800 animate-pulse aspect-[4/3] border border-white/10" />
              ) : (
                <FloorPlanCarousel
                  ref={floorPlanCarouselRef}
                  floorPlans={sortedFloorPlans}
                  availableTables={availableTables}
                  selectedTable={selectedTable}
                  onTableSelect={handleTableClick}
                  onFloorPlanChange={handleFloorPlanChange}
                  pricePerGuest={eventBookingCategory?.price || 0}
                  currency={pageCurrency}
                  inCartTables={inCartTables}
                  localTables={localTables}
                  soldOutCartTables={soldOutCartTables}
                />
              )}
            </div>
          </div>
        )}

        {selectedTimeSlot && viewMode === 'list' && (
          <div className="mt-0">
            <MobileTableListView
              floorPlanItems={allFloorPlanItems}
              availableTables={availableTables}
              selectedTable={selectedTable}
              onTableSelect={handleTableClick}
              inCartTables={inCartTables}
              localTables={localTables}
              soldOutCartTables={soldOutCartTables}
              itemType={displayItemType}
              pricePerGuest={eventBookingCategory?.price || 0}
              currency={pageCurrency}
            />
          </div>
        )}
        </div>

      </main>

      {/* AddOn limited checkout error container (just above floating summary) */}
      <AnimatePresence>
        {addOnErrorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ type: 'spring', bounce: 0.3 }}
            className="fixed top-20 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none"
          >
            <div className="bg-red-500/90 backdrop-blur-md text-white px-4 py-2.5 rounded-full shadow-[0_4px_20px_rgba(239,68,68,0.3)] border border-red-400/30 font-semibold text-sm text-center max-w-sm">
              {addOnErrorMessage}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Price Summary */}
      <MobileTableFloatingSummary
        priceBreakdown={{
          totalPrice: priceBreakdown.totalPrice,
          perGuestBreakdown: priceBreakdown.perSlot.flatMap(slot => 
            slot.perGuestBreakdown.map(guest => ({
              addOns: guest.addOns.map(addOn => ({
                addOn: {
                  add_on: {
                    name: addOn.addOn.add_on.name,
                  },
                },
                price: addOn.price,
              })),
            }))
          ),
        }}
        tableSlots={priceBreakdown.perSlot.map(slot => ({
          tableNumber: slot.tableNumber,
          tableSize: slot.tableSize,
          guestCount: slot.guestCount,
          basePrice: slot.basePrice,
        }))}
        guestCount={priceBreakdown.perSlot.reduce((sum, slot) => sum + slot.guestCount, 0)}
        eventBookingCategory={eventBookingCategory as any}
        onAddToCart={handleCartAction}
        addingToCart={syncingCart}
        buttonText={getButtonText()}
        isUpdateMode={!!(currentCart && hasCartItemForEBC)}
        onUpdateCart={currentCart && hasCartItemForEBC ? handleCartAction : undefined}
        cartId={currentCart?.id || null}
        addOnCount={priceBreakdown.perSlot.reduce((sum, slot) => 
          sum + slot.perGuestBreakdown.reduce((gSum, guest) => 
            gSum + guest.addOns.length, 0), 0)}
        isLoadingCarts={isLoadingCarts}
        hasCartChanges={hasCartChanges}
        hasViewCartButton={!!(currentCart && currentCart.items && currentCart.items.length > 0)}
        hasCartItemForEBC={hasCartItemForEBC}
        currency={pageCurrency}
        itemType={displayItemType}
        onViewCart={() => currentCart && navigate(`/carts/${currentCart.id}`)}
      />

      {/* Slide-Up Popup for Table Details & Addons */}
      <MobileTableSelectionPopup
        isOpen={isPopupOpen}
        onClose={handleBackFromPreview}
        floorPlan={currentFloorPlan}
        selectedTable={selectedTable}
        floorPlanItems={allFloorPlanItems}
        pricePerGuest={eventBookingCategory?.price || 0}
        currency={pageCurrency}
        categoryImageId={eventBookingCategory?.booking_category?.base_image_id}
        selectedTimeSlot={selectedTimeSlot}
        businessTimeZone={timezone}
        tableSize={tableSize}
        itemType={displayItemType}
        guestCount={currentSelection?.guestCount || 0}
        onAddClick={handleAddTableClick}
        addOns={adjustedAddOnSchedules || eventBookingCategory?.add_on_schedules || []}
        selectedAddOns={selectedAddOns}
        onAddOnToggle={(addOnId: string, isSelected: boolean, guestIndex?: number) => {
          if (selectedTable && selectedTimeSlot) {
            handleAddOnToggleForSlot(selectedTable, selectedTimeSlot, addOnId, isSelected, guestIndex);
          }
        }}
        addOnIdsPerGuest={currentSelection?.addOnIdsPerGuest || []}
        onQuantityChange={(addOnId: string, guestIndex: number, quantity: number) => {
          if (selectedTable && selectedTimeSlot) {
            setAddOnQuantityForSlot(selectedTable, selectedTimeSlot, addOnId, guestIndex, quantity);
          }
        }}
        earlyEntryDuration={eventSchedule?.early_entry_duration || null}
      />
    </div>
  );
};

export default TableBookingMobile;

